// The block-type registry for the micro-site builder. Mirrors the backend
// contract in app/models/concerns/microsite_blocks.rb — keep the two in sync.
// Each entry: { type, label, icon, defaultProps() }.
import {
  Heading, Type, Image as ImageIcon, Images, Table2, Minus, MoveVertical, Video,
  Tags as TagsIcon, LayoutGrid, ListChecks,
} from "lucide-react"

let counter = 0
// Stable-enough client id for a new block (server doesn't care about the value).
export function newBlockId() {
  counter += 1
  return `b_${Date.now().toString(36)}_${counter}`
}

export const CONTENT_VERSION = 1
export const RATIOS = ["16:9", "4:3", "1:1"]
export const HEADING_LEVELS = [1, 2, 3]
export const SPACER_SIZES = ["sm", "md", "lg"]

export const BLOCK_DEFS = [
  {
    type: "heading",
    label: "Heading",
    icon: Heading,
    defaultProps: () => ({ text: "Heading", level: 2 }),
  },
  {
    type: "text",
    label: "Text",
    icon: Type,
    defaultProps: () => ({ html: "<p>Describe the property…</p>" }),
  },
  {
    type: "image",
    label: "Image",
    icon: ImageIcon,
    defaultProps: () => ({ image_id: null, ratio: "16:9" }),
  },
  {
    type: "image_gallery",
    label: "Gallery",
    icon: Images,
    defaultProps: () => ({ image_ids: [], ratio: "16:9", show_posted_date: true }),
  },
  {
    type: "video_embed",
    label: "Video",
    icon: Video,
    defaultProps: () => ({ url: "", provider: "", caption: "", ratio: "16:9" }),
  },
  {
    type: "spec_grid",
    label: "Specs",
    icon: Table2,
    defaultProps: () => ({
      items: [
        { label: "Total price", value: "" },
        { label: "Area", value: "" },
        { label: "Facing", value: "" },
      ],
    }),
  },
  {
    type: "tags",
    label: "Tags",
    icon: TagsIcon,
    defaultProps: () => ({ tags: [] }),
  },
  {
    type: "feature_grid",
    label: "Features",
    icon: LayoutGrid,
    defaultProps: () => ({
      items: [
        { icon: "🏠", title: "Spacious homes", text: "" },
      ],
    }),
  },
  {
    type: "amenities",
    label: "Amenities",
    icon: ListChecks,
    defaultProps: () => ({
      groups: [
        { name: "Fitness & Wellness", items: ["Gymnasium", "Yoga"] },
      ],
    }),
  },
  { type: "divider", label: "Divider", icon: Minus, defaultProps: () => ({}) },
  { type: "spacer", label: "Spacer", icon: MoveVertical, defaultProps: () => ({ size: "md" }) },
]

export const BLOCK_DEF_MAP = Object.fromEntries(BLOCK_DEFS.map((b) => [b.type, b]))

export function makeBlock(type) {
  const def = BLOCK_DEF_MAP[type]
  if (!def) return null
  return { id: newBlockId(), type, props: def.defaultProps() }
}

// A sensible starter layout for a brand-new campaign.
export function defaultContent() {
  return {
    version: CONTENT_VERSION,
    blocks: [
      { id: newBlockId(), type: "image_gallery", props: { image_ids: [], ratio: "16:9", show_posted_date: true } },
      { id: newBlockId(), type: "heading", props: { text: "Property title", level: 2 } },
      { id: newBlockId(), type: "spec_grid", props: { items: [
        { label: "Total price", value: "" },
        { label: "Area", value: "" },
        { label: "Facing", value: "" },
      ] } },
      { id: newBlockId(), type: "text", props: { html: "<p>Describe the property…</p>" } },
    ],
  }
}

// Normalize whatever the API returned into { version, blocks: [...] }.
export function normalizeContent(content) {
  if (Array.isArray(content)) return { version: CONTENT_VERSION, blocks: content }
  if (content && Array.isArray(content.blocks)) {
    return { version: content.version || CONTENT_VERSION, blocks: content.blocks }
  }
  return { version: CONTENT_VERSION, blocks: [] }
}
